"use client";

const accentColor = "#9B9DFF";
const font = "'IBM Plex Mono', monospace";

interface LevelRow {
  level: number;
  score: number;
  actions: number;
  base: number;
}

interface GameCard {
  id: string;
  score: string;
  totalActions: number;
  run: string;
  scorecard: string;
  rows: LevelRow[];
}

const games: GameCard[] = [
  {
    id: "ls20",
    score: "69.77%",
    totalActions: 3703,
    run: "ls20-cb3b57cc/09f5aec7-3fcf-41b3-b079-1ed13319b930",
    scorecard: "b8cf9179-4262-462b-8bcc-409204aa205f",
    rows: [
      { level: 1, score: 100.0, actions: 14, base: 29 },
      { level: 2, score: 97.62, actions: 42, base: 41 },
      { level: 3, score: 100.0, actions: 57, base: 172 },
      { level: 4, score: 100.0, actions: 49, base: 49 },
      { level: 5, score: 58.89, actions: 90, base: 53 },
      { level: 6, score: 29.38, actions: 211, base: 62 },
      { level: 7, score: 2.53, actions: 3240, base: 82 },
    ],
  },
  {
    id: "vc33",
    score: "42.87%",
    totalActions: 2092,
    run: "vc33-9851e02b/b432e1b7-39c9-498c-a0b2-df6db4b02855",
    scorecard: "87dccf60-b2b8-4a3a-a7e2-44c9e0b9c745",
    rows: [
      { level: 1, score: 28.57, actions: 21, base: 6 },
      { level: 2, score: 46.43, actions: 28, base: 13 },
      { level: 3, score: 63.27, actions: 49, base: 31 },
      { level: 4, score: 25.32, actions: 233, base: 59 },
      { level: 5, score: 5.74, actions: 1604, base: 92 },
      { level: 6, score: 30.77, actions: 78, base: 24 },
      { level: 7, score: 100.0, actions: 79, base: 82 },
    ],
  },
  {
    id: "ft09",
    score: "39.15%",
    totalActions: 344,
    run: "ft09-9ab2447a/142c3c7f-2a22-46b7-a40e-79209935e674",
    scorecard: "4e218993-60de-448b-ad98-dafa624d7be8",
    rows: [
      { level: 1, score: 30.61, actions: 49, base: 15 },
      { level: 2, score: 100.0, actions: 7, base: 7 },
      { level: 3, score: 28.3, actions: 53, base: 15 },
      { level: 4, score: 34.78, actions: 46, base: 16 },
      { level: 5, score: 25.0, actions: 84, base: 21 },
      { level: 6, score: 16.19, actions: 105, base: 17 },
    ],
  },
];

function actionColor(actions: number, base: number) {
  if (actions <= base) return "#4ade80";
  if (actions >= base * 5) return "#f87171";
  return "rgba(255,255,255,0.85)";
}

export function PerGameBreakdown() {
  return (
    <div style={{ margin: "24px 0", fontFamily: font }}>
      {/* Game cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        {games.map((game) => (
          <div
            key={game.id}
            style={{
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "0.5rem",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                padding: "10px 14px",
                background: "rgba(255,255,255,0.03)",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <span
                style={{ fontSize: "13px", fontWeight: 500, color: "#fff" }}
              >
                {game.id}
              </span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: accentColor,
                }}
              >
                {game.score}
              </span>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "11px",
                  minWidth: "260px",
                }}
              >
                <thead>
                  <tr style={{ color: "rgba(255,255,255,0.55)" }}>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "6px 10px",
                        fontWeight: 400,
                      }}
                    >
                      Level
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "6px 10px",
                        fontWeight: 400,
                      }}
                    >
                      Score
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "6px 10px",
                        fontWeight: 400,
                      }}
                    >
                      Actions
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "6px 10px",
                        fontWeight: 400,
                      }}
                    >
                      Base
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {game.rows.map((row, i) => (
                    <tr
                      key={row.level}
                      style={{
                        background:
                          i % 2 === 0
                            ? "rgba(255,255,255,0.02)"
                            : "transparent",
                      }}
                    >
                      <td
                        style={{
                          padding: "5px 10px",
                          color: "rgba(255,255,255,0.7)",
                        }}
                      >
                        {row.level}
                      </td>
                      <td
                        style={{
                          padding: "5px 10px",
                          textAlign: "right",
                          color: "rgba(255,255,255,0.85)",
                        }}
                      >
                        {row.score.toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: "5px 10px",
                          textAlign: "right",
                          color: actionColor(row.actions, row.base),
                        }}
                      >
                        {row.actions.toLocaleString()}
                      </td>
                      <td
                        style={{
                          padding: "5px 10px",
                          textAlign: "right",
                          color: "rgba(255,255,255,0.55)",
                        }}
                      >
                        {row.base}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      borderTop: "1px solid rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.5)",
                    }}
                  >
                    <td colSpan={2} style={{ padding: "5px 10px" }} />
                    <td
                      style={{
                        padding: "5px 10px",
                        textAlign: "right",
                        fontWeight: 500,
                      }}
                    >
                      {game.totalActions.toLocaleString()}
                    </td>
                    <td style={{ padding: "5px 10px" }} />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Run IDs */}
            <div
              style={{
                padding: "8px 10px",
                marginTop: "auto",
                fontSize: "9px",
                color: "rgba(255,255,255,0.45)",
                borderTop: "1px solid rgba(255,255,255,0.04)",
                lineHeight: 1.6,
              }}
            >
              run: {game.run}
              <br />
              scorecard: {game.scorecard}
            </div>
          </div>
        ))}
      </div>

      {/* Observations */}
      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "0.5rem",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "10px 14px",
            background: "rgba(255,255,255,0.03)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            fontSize: "11px",
            fontWeight: 500,
            color: "#fff",
            letterSpacing: "0.06em",
          }}
        >
          OBSERVATIONS
        </div>
        <ul
          style={{
            padding: "12px 14px 12px 28px",
            margin: 0,
            fontSize: "12px",
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.7,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <li>
            <span style={{ color: "#4ade80" }}>Beats or matches baseline</span>{" "}
            on several levels
          </li>
          <li>
            Performance{" "}
            <span style={{ color: "#f87171" }}>
              drops sharply on later, harder levels
            </span>
            <br />
            <span
              style={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.55)",
                marginLeft: "12px",
              }}
            >
              Agent fails to figure out a new game mechanic, leading to
              excessive actions
            </span>
          </li>
          <li>
            Each score is from the first ever complete run of that game. No
            reruns.
          </li>
          <li>
            Substantial room for improvement
            <br />
            <span
              style={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.55)",
                marginLeft: "12px",
              }}
            >
              e.g. enhanced shared memory system between sub-agents
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
